import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface HelpDrawerState {
  isOpen: boolean;
  openedFromSlug: string | null;
}

interface HelpDrawerApi extends HelpDrawerState {
  openDrawer: (slug?: string) => void;
  closeDrawer: () => void;
  // openOnceForKey checks a localStorage flag; when unset, opens the drawer
  // scrolled to `slug` and flips the flag to true. Idempotent within a
  // session and never blocks the page.
  openOnceForKey: (flagKey: string, slug: string) => void;
}

const HelpDrawerContext = createContext<HelpDrawerApi | null>(null);

function readFlag(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // ignored
  }
}

function isEditableElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  return false;
}

interface Props {
  children: ReactNode;
}

export function HelpDrawerProvider({ children }: Props): JSX.Element {
  const [state, setState] = useState<HelpDrawerState>({
    isOpen: false,
    openedFromSlug: null,
  });
  const lastOpenedRef = useRef<HTMLElement | null>(null);
  const onceFiredKeys = useRef<Set<string>>(new Set());

  const openDrawer = useCallback((slug?: string) => {
    if (typeof document !== 'undefined') {
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        lastOpenedRef.current = active;
      }
    }
    setState({ isOpen: true, openedFromSlug: slug ?? null });
  }, []);

  const closeDrawer = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false }));
    // Restore focus to the element that opened the drawer.
    window.setTimeout(() => {
      const target = lastOpenedRef.current;
      if (target && typeof target.focus === 'function') {
        try {
          target.focus();
        } catch {
          // ignored
        }
      }
    }, 0);
  }, []);

  const openOnceForKey = useCallback(
    (flagKey: string, slug: string) => {
      if (onceFiredKeys.current.has(flagKey)) return;
      onceFiredKeys.current.add(flagKey);
      if (readFlag(flagKey)) return;
      writeFlag(flagKey, true);
      openDrawer(slug);
    },
    [openDrawer],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state.isOpen) {
        e.stopPropagation();
        closeDrawer();
        return;
      }
      if (e.key === '?' && !isEditableElement(document.activeElement)) {
        e.preventDefault();
        if (state.isOpen) {
          closeDrawer();
        } else {
          openDrawer();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.isOpen, openDrawer, closeDrawer]);

  const api = useMemo<HelpDrawerApi>(
    () => ({
      isOpen: state.isOpen,
      openedFromSlug: state.openedFromSlug,
      openDrawer,
      closeDrawer,
      openOnceForKey,
    }),
    [state.isOpen, state.openedFromSlug, openDrawer, closeDrawer, openOnceForKey],
  );

  return <HelpDrawerContext.Provider value={api}>{children}</HelpDrawerContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHelpDrawer(): HelpDrawerApi {
  const ctx = useContext(HelpDrawerContext);
  if (!ctx) {
    throw new Error('useHelpDrawer must be used within <HelpDrawerProvider>');
  }
  return ctx;
}

// useHelpDrawerOptional returns null when called outside a provider — used by
// test fixtures and surfaces that may render before the provider is mounted.
// eslint-disable-next-line react-refresh/only-export-components
export function useHelpDrawerOptional(): HelpDrawerApi | null {
  return useContext(HelpDrawerContext);
}
